/**
 * Skills React Query Hooks — F-SKILL-1 Packet B1 + B2
 *
 * Wraps SkillsService with TanStack Query for caching and invalidation.
 * The list hook uses the proper paginated + filtered list endpoint via
 * SkillsService (src/services/roros/skills.service.ts) which aligns
 * with the actual backend response shape.
 *
 * B2 additions: useRorosSkillDetail, useRorosSkillUpdate, useRorosSkillDelete,
 *               useSkillAttachments, useSkillUploadAttachment, useSkillRemoveAttachment,
 *               useSkillVersions, useSkillVersion, useSkillRestore.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SkillsService, type ListSkillsParams } from '@/services/roros/skills.service';
import { RorosSkillService } from '@/services/RorosService';
import type { CreateSkillPayload, UpdateSkillPayload } from '@/types/roros/skills';
import { rorosKeys } from './keys';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

/** List skills with optional search/filter. Returns paginated {data, meta}. */
export function useRorosSkills(params: ListSkillsParams = {}) {
  return useQuery({
    queryKey: [...rorosKeys.skills.all, params] as const,
    queryFn: () => SkillsService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosSkillMatrix() {
  return useQuery({
    queryKey: rorosKeys.skills.matrix,
    queryFn: () => SkillsService.getMatrix(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosSkill(id: string) {
  return useQuery({
    queryKey: rorosKeys.skills.detail(id),
    queryFn: () => RorosSkillService.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

/** Create a custom skill. Uses the typed CreateSkillPayload from src/types/roros/skills.ts. */
export function useRorosSkillCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSkillPayload) => SkillsService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.all });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.matrix });
    },
  });
}

export function useRorosSkillUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosSkillService.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.all });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.detail(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.matrix });
    },
  });
}

export function useRorosSkillRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosSkillService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.all });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.matrix });
    },
  });
}

export function useRorosSkillAssign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      agentDefinitionId,
    }: {
      id: string;
      agentDefinitionId: string;
    }) => SkillsService.assignSkill(id, agentDefinitionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.matrix });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.all });
    },
  });
}

export function useRorosSkillUnassign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, agentDefinitionId }: { id: string; agentDefinitionId: string }) =>
      SkillsService.unassignSkill(id, agentDefinitionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.matrix });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.all });
    },
  });
}

// ─── B2: Detail query ──────────────────────────────────────────────────────────

export function useRorosSkillDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? rorosKeys.skills.detail(id) : ['roros', 'skills', '__none__'],
    queryFn: () => SkillsService.get(id!),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── B2: Update mutation ───────────────────────────────────────────────────────

export function useRorosSkillUpdateB2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSkillPayload }) =>
      SkillsService.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.all });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.detail(id) });
    },
  });
}

// ─── B2: Delete mutation ───────────────────────────────────────────────────────

export function useRorosSkillDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => SkillsService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.all });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.matrix });
    },
  });
}

// ─── B2: Attachments ──────────────────────────────────────────────────────────

export function useSkillAttachments(skillId: string | null | undefined) {
  return useQuery({
    queryKey: skillId ? rorosKeys.skills.attachments(skillId) : ['roros', 'skills', '__none__', 'attachments'],
    queryFn: () => SkillsService.listAttachments(skillId!),
    enabled: !!skillId,
    staleTime: 30_000,
    gcTime: GC_TIME,
  });
}

export function useSkillUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, file }: { skillId: string; file: File }) =>
      SkillsService.uploadAttachment(skillId, file),
    onSuccess: (_data, { skillId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.attachments(skillId) });
    },
  });
}

export function useSkillRemoveAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, linkId }: { skillId: string; linkId: string }) =>
      SkillsService.removeAttachment(skillId, linkId),
    onSuccess: (_data, { skillId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.attachments(skillId) });
    },
  });
}

// ─── B2: Versions ─────────────────────────────────────────────────────────────

export function useSkillVersions(skillId: string | null | undefined) {
  return useQuery({
    queryKey: skillId ? rorosKeys.skills.versions(skillId) : ['roros', 'skills', '__none__', 'versions'],
    queryFn: () => SkillsService.listVersions(skillId!),
    enabled: !!skillId,
    staleTime: 30_000,
    gcTime: GC_TIME,
  });
}

export function useSkillVersion(skillId: string | null | undefined, versionId: string | null | undefined) {
  return useQuery({
    queryKey: skillId && versionId
      ? rorosKeys.skills.version(skillId, versionId)
      : ['roros', 'skills', '__none__', 'versions', '__none__'],
    queryFn: () => SkillsService.getVersion(skillId!, versionId!),
    enabled: !!skillId && !!versionId,
    staleTime: 5 * 60_000,
    gcTime: GC_TIME,
  });
}

export function useSkillRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, versionId }: { skillId: string; versionId: string }) =>
      SkillsService.restoreVersion(skillId, versionId),
    onSuccess: (_data, { skillId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.skills.all });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.detail(skillId) });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.attachments(skillId) });
      qc.invalidateQueries({ queryKey: rorosKeys.skills.versions(skillId) });
    },
  });
}
